import React, { useRef, useEffect, useCallback, useState} from 'react';
import SourceFinder from './SourceFinder.js';
import {trim} from "utils/trim.js"
import ContentDropdown from "components/dropdowns/ContentDropdown.js"
import ConditionedCheckmark from "components/ConditionedCheckmark.js";
import {
    SOUND_RATED,
    PRESET_RATED} from 'decentralized/xanadu/LinkTypes.js';
import {useFavorites} from "components/hooks/useFavorites.js"
import TagsInfo from "components/installation/TagsInfo.js";
import {usePacks} from './usePacks.js';
import {
    setCreatingNewPack
} from 'actions/index.js';
import {getPacks} from 'packs/packs.js';
import {useScrollTo} from './useScrollTo.js';
import ethereum from 'decentralized/ethereum.js';
import {FILTER_BY_TAG, ADD_SOUND_BUTTON, SELECTOR_WAVEFORM} from 'constants/contextual-help.js';
import Documented from 'components/help/Documented.js';
import PulseLoader from 'react-spinners/PulseLoader'
import {sleep} from 'utils/sleep.js';
import {getProjectsUsed} from 'utils/getProjectsUsed.js';
import Rating from './Rating.js';
import Tone from 'tone';
import AudioUnit from 'engine/audio.js';
import Waveform from 'components/Waveform.js';
import SampleUsed from 'components/installation/SampleUsed.js';
import Sample from 'repos/Sample.js';
import Colors from 'components/Colors.js';
import {connect} from 'react-redux';
import {
    SEQUENCES
    } from 'constants/sidebar-view-modes.js';
import {
    setCurrentSoundInList,
    setExpandedProject,
    setSidebarViewMode,
    setSampleDragging,
    setQuickAddSearchText
} from 'actions/index.js';

const colors = new Colors();
const HOVER_TO_LOAD_TIME = 750;

const SampleOption = ({
    isCurrent,
    isNext,
    setCurrentSoundInList,
    setPackToSearch,
    number,
    setExpandedProject,
    setSidebarViewMode,
    result,
    scrollRef,
    sequencerEngine,
    setSampleDragging,
    source,
    setSearchText,
    autoLoad=false,
    setCreatingNewPack
}) => { 
    let hovered = useRef(false);
    let playOnLoad = useRef(false);
    let added = useRef(false);
    let loading = useRef(false);
    let ref = useRef(undefined);
    let [showProjects, setShowProjects] = useState(undefined);
    let [loadedAudio, setLoadedAudio] = useState(undefined);
    let stopped = useRef(false);
    let disposed = useRef(false);
    let [isSourcing, setIsSourcing] = useState(false);
    let [dropdownPos, setDropdownPos] = useState(undefined);
    let [isLoading, setIsLoading] = useState(false);
    let [sample, setSample] = useState(undefined);
    let [showSample, setShowSample] = useState(false);
    let [projectsUsed, setProjectsUsed] = useState([]);
    let [selectedSlice, setSelectedSlice] = useState(undefined);
    let [isTagging, setIsTagging] = useState(undefined);
    let {toggleFavorite, isFavorited} = useFavorites({type: SOUND_RATED, contentHash: sample && sample.getId()});
    let [guildName, setGuildName] = useState(undefined);

    useEffect(() => {
        if (result.guildId) {
            ethereum.guilds.getGuildName(result.guildId)
                .then(
                    setGuildName);
        }
    }, [result]);

    let audioUnit = useRef(undefined);
    const isResample = sample && sample.tags && sample.tags.indexOf("Resampled") > -1;
    let id =  isResample && projectsUsed.length > 0 ?
        projectsUsed[projectsUsed.length - 1].newSequence :
        source;
    let color = id && colors.getColorForHash(id);
    let style = {
        borderColor: color,
        backgroundColor: color,
    };

    useEffect(() => {
        if (isNext) {
            loadSample();
        }
    }, [isNext]);

    useEffect(() => {
        if (isCurrent) {
            stopped.current = false;
            if (loadedAudio) {
                audioUnit.current.playSound(0);
            } else {
                setIsLoading(true);
                if (loading.current) {
                    playOnLoad.current = true;
                } else {
                    loadSample(true);
                }
            }
        } else {
            stopped.current = true;
            if (audioUnit.current) {
                audioUnit.current.stopSound();
            } 
            setIsTagging(false);
        }
    }, [isCurrent, setLoadedAudio, sample, setIsTagging]);

    useScrollTo({ref, scrollRef, isCurrent});

    useEffect(() => {
        if (isCurrent) {
            window.addEventListener("keydown", onKeyDown);
        } else {
            window.removeEventListener("keydown", onKeyDown);
        }
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isTagging, setIsTagging, isCurrent, sample, showSample, sequencerEngine, selectedSlice, setShowSample]);

    const onKeyDown = useCallback((e) => {
        if (e.key === 'Enter' && !added.current) {
            if (isTagging) { return; }
            added.current=true;
            let selectedTrack = sequencerEngine.getSelectedTrack();
            if (selectedTrack && (e.ctrlKey || e.metaKey)) {
                selectedTrack.useNewSound(sample);
            } else {
                addSample(true);
            }
           
            if (audioUnit.current) {
                audioUnit.current.stopSound();
            }
        } else if (e.key === 'Backspace') {
            if (isTagging) { return; }
            if (audioUnit.current) {
                audioUnit.current.stopSound();
            }
        } else if (e.key === 't' && (e.ctrlKey || e.metaKey)) {
            setIsTagging(!isTagging);
        } else if (e.key === 'f' && (e.ctrlKey || e.metaKey)) {
            toggleFavorite(e);
        }
    }, [isTagging, setIsTagging, isCurrent, sample, showSample, sequencerEngine, selectedSlice, setShowSample]);

    useEffect(() => {
        if (autoLoad && sample) {
            loadSample();
            setShowSample(true);
        }
    }, [sample, autoLoad, setLoadedAudio, setShowSample]);

    useEffect(() => {
        let sample = new Sample(result.ipfsHash);
        sample.load(false)
            .then(() => setSample(sample));
    }, [result, setSample, audioUnit]);

    useEffect(() => {
        getProjectsUsed(result.ipfsHash)
            .then(setProjectsUsed);
    }, [setProjectsUsed, result]);

    useEffect(() => {
        return () => {
            if (audioUnit.current) {
                audioUnit.current.dispose();
            }
            if (sample) {
                sample.dispose();
            }
            disposed.current = true;
        };
    }, []);

    const loadProject = (project) => {
        setExpandedProject({... project, edits: []});
        setSidebarViewMode(SEQUENCES);
    };

    const addSample = useCallback((force, e) => {
        if (e && e.ctrlKey) {
            return;
        }
        if (showSample || force) {
            sequencerEngine.addSound(sample, undefined, selectedSlice && [selectedSlice]);
        } else {
            setShowSample(true);
            if (!loading.current) {
                loadSample(true);
            } else if (audioUnit.current) {
                audioUnit.current.playSound(0, 2);
            }
        }
    }, [sample, showSample, sequencerEngine, selectedSlice, setShowSample]);

    const loadSample = useCallback((play=false) => {
        if (sample && !sample.audioBuffer && !loading.current) {
            loading.current = true;
            sample.fetchAudio()
                .then(x => {
                    if (disposed.current) { return; }
                    let _audioUnit = new AudioUnit(Tone.context);
                    loading.current = false;
                    _audioUnit.setBuffer(x.audioBuffer);
                    audioUnit.current = _audioUnit;
                    if (play || playOnLoad.current) {
                        if (!stopped.current) {
                            _audioUnit.playSound(0);
                        }
                    }
                    setLoadedAudio(true);
                });
        } 
   }, [setLoadedAudio, sample]);

    const onHover = useCallback(async () => {
        hovered.current = true;
        await sleep(HOVER_TO_LOAD_TIME);
        if (!hovered.current) {
            return;
        }
        loadSample();
    }, [sample, showSample, setLoadedAudio]);

    const favoriteSample = useCallback(() => {
    }, [sample]);

    const onContextMenu = useCallback((e) => {
        e.stopPropagation();
        let x = e.clientX - ref.current.getBoundingClientRect().left;
        let y = e.clientY - ref.current.getBoundingClientRect().top;
        if (x > 160) {
            x -= 200;
        }
        setDropdownPos({x,y});
        e.preventDefault();
    }, [setDropdownPos]);


    const onMouseDown = useCallback((e) => {
       setCurrentSoundInList(number);
    }, [number]);

    let className = "sample-option";
    if (isCurrent) {
        className += " current";
    }

    return (
        <div
          ref={ref}
          onContextMenu={onContextMenu}
          onMouseDown={onMouseDown}
          onMouseOver={onHover}
          draggable={true}
          onDragOver={(e) => e.preventDefault()}
          onDragStart={(e) => {
              if (audioUnit.current) {
                  audioUnit.current.stopSound();
              }
              stopped.current = true;
              
              setSampleDragging(sample);
          }}
          onMouseLeave={() => hovered.current = false}
          className={className}>
         <ContentDropdown
            dropdownPos={dropdownPos}
            setDropdownPos={setDropdownPos}
            setIsTagging={setIsTagging}
           setIsSourcing={setIsSourcing}
            isUnsourced={true}
            content={sample}
            setPackToSearch={setPackToSearch}
            contentHash={sample && sample.getId()}/>
         <div className="sample-option-inner">
            <div
              className="wave-and-name">
              <div className="name-and-tags">
                {isFavorited ? <div className="heart favorited"/> : ""}
                <div
                  onClick={(e) => addSample(undefined, e)}
                  className="sample-name">{truncate(trim(result.title))}</div>
                {guildName ? <div className="guild-name">{guildName}</div> : ''}
                 <PulseLoader
                   sizeUnit={"px"}
                   style={{margin: 0, lineHeight: '12px'}}
                   size={6}
                   color={'white'}
                   loading={loading.current}
                 />
                <div className="pack-indicators">
                  {sample && sample.packs && sample.packs.map(
                      pack => <div style={{backgroundColor: colors.getColorForHash(pack)}} className="pack-thumb"/>)}
                </div>
                {projectsUsed[0] ?
                 <div
                   onClick={() => loadProject(projectsUsed[0])}
                   className="projects-used">
                   <span>
                     {projectsUsed.length} 
                   </span>
                 </div> :
                 ""}
                <div style={!isResample || projectsUsed.length === 0 ? {maxWidth: 200} : {}} className="sample-tags">
                  {!isTagging && sample && sample.tags && sample.tags
                   .map(
                      tag =>
                          <Documented key={tag} element={FILTER_BY_TAG} arg={tag}>
                            <div
                              style={{
                                  borderColor: colors.getColorForHash(tag),
                                  color: colors.getColorForHash(tag),
                              }}
                              onClick={(e) => {
                                  e.stopPropagation();
                                  setSearchText(tag);
                              }}
                              className="tag-option">
                              {truncate(tag, 20)}
                            </div>
                          </Documented>
                  )}
                  {sample && sample.year ? <div className="sample-year">{sample.year}</div> : ''}
                </div>
              </div>
              {showSample ?
               <div className="waveform-and-rating">
                 {loadedAudio ?
                  <Documented element={SELECTOR_WAVEFORM}>
                    <Waveform
                      selections={[]} 
                      onChooseSlice={setSelectedSlice}
                      buffer={audioUnit.current.buffer}
                      height={40}
                      width={340}
                      audioUnit={audioUnit.current}
                      sample={sample}
                      id={sample.ipfsHash}
                      sequencerEngine={sequencerEngine}
                    />
                  </Documented>
                  : ''}
              </div> : ''}
              {isTagging ? <TagsInfo  content={sample}/> : ''}
              {isSourcing ? <SourceFinder setIsSourcing={setIsSourcing} sample={sample} guildId={result.guildId} /> : ''}
            </div>
          </div>
        </div>
    );
};

const truncate = (name, len=30) => name.length > len ? name.slice(0, len) + '...' : name;

const mapDispatchToProps = (dispatch) => ({
    setSampleDragging: sample => dispatch(setSampleDragging(sample)),
    setSearchText: x => dispatch(setQuickAddSearchText(x)),
    setSidebarViewMode: m => dispatch(setSidebarViewMode(m)),
    setExpandedProject: m => dispatch(setExpandedProject(m)),
    setCurrentSoundInList: m => dispatch(setCurrentSoundInList(m)),
    setCreatingNewPack: m => dispatch(setCreatingNewPack(m)),
});

export default connect(null, mapDispatchToProps)(SampleOption);
